import {
  getInspectionsOptions,
  getJurisdictionsOptions,
  getNonRegistrantLicenseStatsOptions,
  getUnregisteredPropertiesOptions,
} from "@/hera-api/client/@tanstack/react-query.gen";
import { getManualCheckPayments } from "@/hera-api/manual-check-payments-api";
import {
  DASHBOARD_SECTIONS_SKIP_INSPECTIONS,
  DASHBOARD_SECTIONS_SKIP_LICENSE_STATS,
  DASHBOARD_SECTIONS_SKIP_REMITTANCE,
  DASHBOARD_SECTIONS_SKIP_UNREGISTERED,
  dashboardListQueryOptions,
} from "@/lib/dashboard-query-options";
import { buildOrdinanceRowsFromOperatorJurisdictions } from "@/lib/ordinance-list-rows";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { regionQueryParams } from "@/hera-api/regions-api";

import { useStreamingNdjson } from "./use-streaming-ndjson";

export function useOperatorDashboardStats(
  isAuthSetupComplete: boolean,
  selectedJurisdictions: string[],
  activeSection: string,
  selectedLicenseTypes: string[] = [],
  selectedRegionIds: string[] = [],
) {
  type RemittanceTrendRow = {
    bucket: string;
    hera_registration_remittance: number;
    hera_late_fee_remittance: number;
    hera_inspection_remittance: number;
  };
  // Top query to be the list of jurisdictions for the top-level filter
  const {
    data: jurisdictionsData,
    isLoading: isJurisdictionsQueryLoading,
    error: jurisdictionsQueryError,
  } = useQuery({
    ...getJurisdictionsOptions(),
    enabled: isAuthSetupComplete,
    ...dashboardListQueryOptions,
  });

  const loadLicenseStats =
    isAuthSetupComplete &&
    !!jurisdictionsData &&
    !DASHBOARD_SECTIONS_SKIP_LICENSE_STATS.has(activeSection);
  const loadUnregistered =
    isAuthSetupComplete &&
    !!jurisdictionsData &&
    !DASHBOARD_SECTIONS_SKIP_UNREGISTERED.has(activeSection);
  const loadInspections =
    isAuthSetupComplete &&
    !!jurisdictionsData &&
    !DASHBOARD_SECTIONS_SKIP_INSPECTIONS.has(activeSection);
  const loadRemittance =
    isAuthSetupComplete &&
    !!jurisdictionsData &&
    !DASHBOARD_SECTIONS_SKIP_REMITTANCE.has(activeSection);

  const {
    data: licenseStats,
    isLoading: isLicensesQueryLoading,
    error: licensesQueryError,
    refetch: refetchLicenseStats,
  } = useQuery({
    ...getNonRegistrantLicenseStatsOptions({
      query: {
        jurisdiction_ids:
          selectedJurisdictions.length > 0 ? selectedJurisdictions : undefined,
        license_type_ids:
          selectedLicenseTypes.length > 0 ? selectedLicenseTypes : undefined,
        ...regionQueryParams(selectedRegionIds),
      },
    }),
    enabled: loadLicenseStats,
    ...dashboardListQueryOptions,
  });

  // Unregistered properties query to get data for stats calculations
  const {
    data: unregisteredData,
    isLoading: isUnregisteredQueryLoading,
    error: unregisteredQueryError,
  } = useQuery({
    ...getUnregisteredPropertiesOptions({
      query: {
        jurisdiction_ids: selectedJurisdictions,
        ...regionQueryParams(selectedRegionIds),
      },
    }),
    enabled: loadUnregistered,
    ...dashboardListQueryOptions,
  });

  // Inspections query to get data for stats calculations
  const {
    data: inspectionsData,
    isLoading: isInspectionsQueryLoading,
    error: inspectionsQueryError,
  } = useQuery({
    ...getInspectionsOptions({
      query: {
        jurisdiction_ids:
          selectedJurisdictions.length > 0 ? selectedJurisdictions : undefined,
        include_deleted: false,
        ...regionQueryParams(selectedRegionIds),
      },
    }),
    enabled: loadInspections,
    ...dashboardListQueryOptions,
  });

  const filteredJurisdictions = useMemo(() => {
    if (!jurisdictionsData?.jurisdictions) return [];

    // Non-deleted LTCs only are loaded on each jurisdiction. If none remain (never
    // configured or all configs soft-deleted), the record still appears in GET
    // /jurisdictions but cannot be "Active" on the jurisdictions dashboard — that
    // mismatch inflated the stat tile vs counting unique active jurisdictions there.
    const withAtLeastOneConfig = jurisdictionsData.jurisdictions.filter(
      (j) => (j.license_type_configurations?.length ?? 0) > 0,
    );

    if (selectedLicenseTypes.length === 0) return withAtLeastOneConfig;

    return withAtLeastOneConfig.filter((jurisdiction) =>
      selectedLicenseTypes.some((licenseTypeId) =>
        jurisdiction.license_type_configurations?.some(
          (config) => config.license_type_id === licenseTypeId,
        ),
      ),
    );
  }, [jurisdictionsData?.jurisdictions, selectedLicenseTypes]);

  // Calculate filtered jurisdictions count based on license type configurations
  const totalJurisdictionsCount = useMemo(() => {
    return filteredJurisdictions.length;
  }, [filteredJurisdictions]);

  // Calculate unregistered properties count
  const totalUnregisteredProperties = useMemo(() => {
    if (!unregisteredData?.unregistered) return 0;

    const activeUnregistered = unregisteredData.unregistered.filter(
      (u) => !u.is_deleted,
    );

    // If no license types are selected, include all active unregistered properties
    if (selectedLicenseTypes.length === 0) return activeUnregistered.length;

    // Only include unregistered properties that match selected license types
    return activeUnregistered.filter((u) =>
      selectedLicenseTypes.includes(u.license_type.id),
    ).length;
  }, [unregisteredData?.unregistered, selectedLicenseTypes]);

  // Calculate active inspections count (INSPECTION_PENDING status)
  const activeInspectionsCount = useMemo(() => {
    if (!inspectionsData?.inspections) return 0;
    return inspectionsData.inspections.filter(
      (inspection) => inspection.status === "INSPECTION_PENDING",
    ).length;
  }, [inspectionsData?.inspections]);

  // Inspections submitted by field users awaiting operator/government review
  const inspectionsPendingReviewCount = useMemo(() => {
    if (!inspectionsData?.inspections) return 0;
    return inspectionsData.inspections.filter(
      (inspection) => inspection.status === "COMMITTED",
    ).length;
  }, [inspectionsData?.inspections]);

  const { data: manualChecksData } = useQuery({
    queryKey: ["manual-check-payments", selectedJurisdictions],
    queryFn: () =>
      getManualCheckPayments(
        selectedJurisdictions.length > 0 ? selectedJurisdictions : undefined,
      ),
    enabled: isAuthSetupComplete,
    placeholderData: keepPreviousData,
  });

  const awaitingChecksCount = useMemo(() => {
    if (!manualChecksData?.checks) return 0;
    return manualChecksData.checks.filter((c) => c.status === "Awaiting Check")
      .length;
  }, [manualChecksData?.checks]);
  const remittanceQuery = useMemo(
    () => ({
      jurisdiction_ids:
        selectedJurisdictions.length > 0 ? selectedJurisdictions : undefined,
      license_type_ids:
        selectedLicenseTypes.length > 0 ? selectedLicenseTypes : undefined,
      count: "12",
    }),
    [selectedJurisdictions, selectedLicenseTypes],
  );
  const { rows: remittanceRows } = useStreamingNdjson<RemittanceTrendRow>({
    path: "/operator/portal/trends/remittance",
    query: remittanceQuery,
    enabled: loadRemittance,
  });
  const totalYTDHeraRemittance = useMemo(() => {
    const currentYear = new Date().getUTCFullYear();
    const isCurrentYearBucket = (bucket: string) => {
      const maybeYear = Number.parseInt(bucket.slice(-4), 10);
      return Number.isFinite(maybeYear) && maybeYear === currentYear;
    };
    return remittanceRows.reduce(
      (sum, row) =>
        isCurrentYearBucket(row.bucket)
          ? sum +
            (row.hera_registration_remittance ?? 0) +
            (row.hera_late_fee_remittance ?? 0) +
            (row.hera_inspection_remittance ?? 0)
          : sum,
      0,
    );
  }, [remittanceRows]);

  /** Rows in the Ordinances workspace (live + historic), matching dashboard filter scope. */
  const ordinanceListRowCount = useMemo(() => {
    if (!jurisdictionsData?.jurisdictions) return 0;
    let rows = buildOrdinanceRowsFromOperatorJurisdictions(
      jurisdictionsData.jurisdictions,
    );
    if (selectedJurisdictions.length > 0) {
      const idSet = new Set(selectedJurisdictions);
      rows = rows.filter((r) => idSet.has(r.jurisdictionId));
    }
    if (selectedLicenseTypes.length > 0) {
      const ltSet = new Set(selectedLicenseTypes);
      rows = rows.filter((r) => ltSet.has(r.licenseTypeId));
    }
    return rows.length;
  }, [
    jurisdictionsData?.jurisdictions,
    selectedJurisdictions,
    selectedLicenseTypes,
  ]);

  return {
    // Data
    jurisdictionsData,
    unregisteredData,
    filteredJurisdictions,

    // Loading states
    isJurisdictionsQueryLoading,
    isLicensesQueryLoading,
    isUnregisteredQueryLoading,
    isInspectionsQueryLoading,

    // Error states
    jurisdictionsQueryError,
    licensesQueryError,
    unregisteredQueryError,
    inspectionsQueryError,

    // Computed stats
    pendingReviewCount: licenseStats?.pending_review_count ?? 0,
    disputedCount: licenseStats?.disputed_count ?? 0,
    refundedCount: licenseStats?.refunded_count ?? 0,
    totalYTDPaidAmount: Number.parseFloat(
      licenseStats?.total_ytd_paid_amount ?? "0",
    ),
    totalRegistrationsCount: licenseStats?.total_registrations ?? 0,
    totalJurisdictionsCount,
    totalUnregisteredProperties,
    activeInspectionsCount,
    inspectionsPendingReviewCount,
    awaitingChecksCount,
    totalYTDHeraRemittance,
    ordinanceListRowCount,
    refetchLicenseStats,

    /** For dashboard shell loading: only block on fetches the active tab needs. */
    isDashboardShellLoading:
      isJurisdictionsQueryLoading ||
      (loadUnregistered && isUnregisteredQueryLoading) ||
      (loadInspections && isInspectionsQueryLoading),
  };
}
